import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle
} from '@/components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Loader2, Trash2 } from 'lucide-react';
import { ExportDispatchScheduleTab } from './ExportDispatchScheduleTab';
import { LocationChangeModal } from './LocationChangeModal';
import { useBroadcastSync, SYNC_TYPES } from '@/hooks/useBroadcastSync';
import { useWebSocketSubscribe } from '@/context/WebSocketContext';
import { useUnsavedChanges, buildConfirmClose } from '@/hooks/useUnsavedChanges';

// Extracted edit-export sheet sub-components (Tabs + Dialog).
import {
  StatusHistoryTab,
  DocumentsTab,
  NotesTab,
  EventLogsTab,
  EditLogsTab,
  DeleteConfirmDialog,
} from './components/editexport';

// Main Edit Export Sheet Component
export const EditExportSheet = ({ open, onOpenChange, exportData, masterData, customers, onSave, onDelete }) => {
  const { api, user } = useAuth();
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState('shipment-status');
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Track unsaved edits — drives the "● Unsaved" pill in the sheet header
  // and the confirm-on-close guard. Gate tracking on `open && exportData`
  // so we don't flash dirty during the initial setFormData() call.
  const trackingReady = !!exportData && open;
  const isDirty = useUnsavedChanges(formData, trackingReady);
  const handleConfirmClose = buildConfirmClose(isDirty, () => onOpenChange(false));
  
  // Status history
  const [statusHistory, setStatusHistory] = useState([]);
  
  // Location Change Modal (same as Import dashboard)
  const [locationChangeModalOpen, setLocationChangeModalOpen] = useState(false);
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Documents
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Notes
  const [notes, setNotes] = useState([]);
  
  // Event Logs (dispatch logs)
  const [eventLogs, setEventLogs] = useState([]);
  // Unified export timeline (drives the in-load Timeline section in Event Logs tab)
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState(null);
  
  // Edit Logs (field change history)
  const [editLogs, setEditLogs] = useState([]);

  // Load export data when opened.
  // Depend on exportData?.id (not the full object) so unrelated parent
  // re-renders don't clobber in-progress edits. Customer-matching is in its
  // own effect so the customers list loading later doesn't reset the form.
  useEffect(() => {
    if (open && exportData) {
      setFormData({
        ...exportData,
        customer_id: exportData.customer_id || '',
        address_id: exportData.address_id || '',
        booking_number: exportData.booking_number || '',
        mbl_number: exportData.mbl_number || '',
        container_number: exportData.container_number || '',
        container_size: exportData.container_size || '',
        ssl: exportData.ssl || '',
        vessel_name: exportData.vessel_name || '',
        destination_port: exportData.destination_port || '',
        cutoff_date: exportData.cutoff_date || '',
        erd: exportData.erd || '',
        load_rdy: exportData.load_rdy || '',
        load_return: exportData.load_return || '',
        status: exportData.status || 'New Shipment',
        driver_id: exportData.driver_id || '',
        remarks: exportData.remarks || '',
        pickup_terminal_id: exportData.pickup_terminal_id || '',
        return_terminal_id: exportData.return_terminal_id || '',
        yard_id: exportData.yard_id || '',
        chassis_type: exportData.chassis_type || '',
        chassis_number: exportData.chassis_number || '',
        shipper_name: exportData.shipper_name || '',
        shipper_address: exportData.shipper_address || '',
        delivery_address: exportData.delivery_address || '',
        delivery_city: exportData.delivery_city || '',
        delivery_state: exportData.delivery_state || '',
        delivery_zip: exportData.delivery_zip || ''
      });
      setDocuments(exportData.documents || []);

      fetchStatusHistory();
      fetchNotes();
      fetchEventLogs();
      fetchExportTimeline();
      fetchEditLogs();
      setActiveTab('shipment-status');
    }
  }, [open, exportData?.id]);

  // Resolve selected customer asynchronously without stomping over form edits
  useEffect(() => {
    if (formData.customer_id && customers.length > 0) {
      const customer = customers.find(c => c.id === formData.customer_id);
      setSelectedCustomer(customer || null);
    } else {
      setSelectedCustomer(null);
    }
  }, [formData.customer_id, customers]);

  const fetchStatusHistory = async () => {
    if (!exportData?.id) return;
    try {
      const response = await api.get(`/exports/${exportData.id}/status-history`);
      setStatusHistory(response.data || []);
    } catch (error) {
      console.error('Failed to fetch status history');
    }
  };

  const fetchNotes = async () => {
    if (!exportData?.id) return;
    try {
      const response = await api.get(`/exports/${exportData.id}/notes`);
      setNotes(response.data || []);
    } catch (error) {
      setNotes([]);
    }
  };

  const fetchEventLogs = async () => {
    if (!exportData?.id) return;
    try {
      const response = await api.get(`/dispatches/logs/export/${exportData.id}`);
      setEventLogs(response.data || []);
    } catch (error) {
      console.error('Failed to fetch event logs:', error);
      setEventLogs([]);
    }
  };

  // Unified export timeline — same data shape as the import timeline.
  // Debounced so a burst of sibling fetches (save → docs → dispatch refresh)
  // only triggers a single network call.
  const exportTimelineFetchTimer = useRef(null);
  // Cancel any pending debounced fetch when the sheet unmounts so we don't fire
  // a stale request after the user closes the drawer.
  useEffect(() => () => {
    if (exportTimelineFetchTimer.current) clearTimeout(exportTimelineFetchTimer.current);
  }, []);
  const fetchExportTimeline = useCallback(() => {
    if (!exportData?.id) return;
    if (exportTimelineFetchTimer.current) clearTimeout(exportTimelineFetchTimer.current);
    exportTimelineFetchTimer.current = setTimeout(async () => {
      setTimelineLoading(true);
      setTimelineError(null);
      try {
        const response = await api.get(`/exports/${exportData.id}/timeline`);
        setTimelineEvents(Array.isArray(response.data) ? response.data : []);
      } catch (e) {
        setTimelineError(e?.response?.data?.detail || 'Failed to load timeline');
        setTimelineEvents([]);
      } finally {
        setTimelineLoading(false);
      }
    }, 250);
  }, [exportData?.id, api]);

  // ---- Live timeline updates (mirrors EditLoadSheet) ----
  // (a) Cross-tab BroadcastChannel: another tab here edits the export.
  // (b) Server-pushed WebSocket: another user fires a dispatch / posts a note.
  // Both pipe into the debounced fetcher → amber pulse on arrival.
  useBroadcastSync(useCallback((msg) => {
    if (!exportData?.id || !msg) return;
    if (msg.type !== SYNC_TYPES.SHIPMENT_UPDATED) return;
    // EXPORT_UPDATED isn't a sync type today; if the dashboard adopts one we'll
    // accept it here too. For now we only match on the generic SHIPMENT_UPDATED
    // payload that other tabs might broadcast for export edits.
    if (msg.payload?.exportId !== exportData.id && msg.payload?.shipmentId !== exportData.id) return;
    fetchExportTimeline();
  }, [exportData?.id, fetchExportTimeline]));

  useWebSocketSubscribe(useCallback((data) => {
    if (!exportData?.id || !data?.type) return;
    // Accept both flat (`data.export_id`) and nested (`data.export.id`) shapes
    // the server might emit — mirrors EditLoadSheet for shape parity.
    const sameExport =
      data.export_id === exportData.id
      || data.export?.id === exportData.id
      || data.shipment_id === exportData.id
      || data.shipment?.id === exportData.id
      || data.dispatch?.export_id === exportData.id
      || data.message?.export_id === exportData.id;
    if (!sameExport) return;
    const interesting = [
      'new_dispatch', 'dispatch_response',
      'export_updated', 'shipment_updated', 'status_updated',
      'comment_added', 'comment_updated', 'comment_deleted',
    ];
    if (interesting.includes(data.type)) {
      fetchExportTimeline();
      if (data.type === 'new_dispatch' || data.type === 'dispatch_response') {
        fetchEventLogs();
      }
    }
  }, [exportData?.id, fetchExportTimeline]));

  const fetchEditLogs = async () => {
    if (!exportData?.id) return;
    try {
      const response = await api.get(`/exports/${exportData.id}/edit-logs`);
      setEditLogs(response.data || []);
    } catch (error) {
      console.error('Failed to fetch edit logs:', error);
      setEditLogs([]);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), 'MM/dd/yyyy hh:mm a');
    } catch {
      return dateStr;
    }
  };

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setSelectedCustomer(customer || null);
    setFormData(prev => ({ 
      ...prev, 
      customer_id: customerId, 
      customer_name: customer?.name || '',
      address_id: '',
      delivery_address: '',
      delivery_city: '',
      delivery_state: '',
      delivery_zip: ''
    }));
  };

  const handleAddressChange = (addressId) => {
    if (!selectedCustomer) return;
    const address = selectedCustomer.addresses?.find(a => a.id === addressId);
    if (address) {
      setFormData(prev => ({
        ...prev,
        address_id: addressId,
        delivery_address: address.address || '',
        delivery_city: address.city || '',
        delivery_state: address.state || '',
        delivery_zip: address.zip_code || ''
      }));
    }
  };

  // Returns true on success, false on failure. Save-and-Exit reads this to
  // decide whether to close. The regular Save button (which already closes
  // on success via onOpenChange below) is unchanged in behaviour.
  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.put(`/exports/${exportData.id}`, formData);
      toast.success('Export updated successfully');
      // Use backend response to keep parent state in sync with DB
      onSave?.(response.data || formData);
      onOpenChange(false);
      return true;
    } catch (error) {
      toast.error('Failed to update export');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/exports/${exportData.id}`);
      toast.success('Export deleted successfully');
      onDelete?.(exportData.id);
      setDeleteDialogOpen(false);
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to delete export');
    } finally {
      setDeleting(false);
    }
  };

  const openLocationChangeModal = () => {
    setLocationChangeModalOpen(true);
  };

  const handleLocationStatusUpdate = () => {
    // LocationChangeModal already saved to backend - just refresh local data
    setLocationChangeModalOpen(false);
    fetchStatusHistory();
    // Re-fetch the export to get updated status/driver
    api.get(`/exports/${exportData.id}`).then(response => {
      if (response.data) {
        setFormData(prev => ({
          ...prev,
          status: response.data.status || prev.status,
          driver_id: response.data.driver_id || prev.driver_id,
          driver_name: response.data.driver_name || prev.driver_name
        }));
      }
    }).catch(() => {});
  };

  const handleDeleteStatusEntry = async (entryId) => {
    try {
      await api.delete(`/exports/${exportData.id}/status-history/${entryId}`);
      toast.success('Status entry deleted');
      fetchStatusHistory();
    } catch (error) {
      toast.error('Failed to delete status entry');
    }
  };

  const handleDocumentUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const uploadedDocs = [];
    const failedUploads = [];
    
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        try {
          const formDataObj = new FormData();
          formDataObj.append('file', file);
          const response = await api.post(`/exports/${exportData.id}/documents`, formDataObj, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          uploadedDocs.push(response.data.document);
        } catch (error) {
          failedUploads.push(file.name);
        }
      }
      
      if (uploadedDocs.length > 0) {
        setDocuments(prev => [...prev, ...uploadedDocs]);
        toast.success(`${uploadedDocs.length} document(s) uploaded`);
      }
      
      if (failedUploads.length > 0) {
        toast.error(`Failed to upload: ${failedUploads.join(', ')}`);
      }
    } catch (error) {
      toast.error('Failed to upload documents');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDocumentDelete = async (docId) => {
    try {
      await api.delete(`/exports/${exportData.id}/documents/${docId}`);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      toast.success('Document deleted');
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleDocumentDownload = async (doc) => {
    try {
      const response = await api.get(`/exports/${exportData.id}/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename || doc.name || 'document');
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleDocumentPreview = async (doc) => {
    try {
      const response = await api.get(`/exports/${exportData.id}/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Failed to preview document');
    }
  };

  const handleAddNote = async (content) => {
    try {
      const response = await api.post(`/exports/${exportData.id}/notes`, { content });
      setNotes(prev => [response.data, ...prev]);
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await api.delete(`/exports/${exportData.id}/notes/${noteId}`);
      setNotes(prev => prev.filter(n => n.id !== noteId));
      toast.success('Note deleted');
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const ssls = masterData?.ssls || [];

  if (!exportData) return null;

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => { if (!o) { handleConfirmClose(); } else { onOpenChange(true); } }}>
        <SheetContent className="w-full sm:max-w-2xl bg-slate-900 border-slate-800 overflow-y-auto p-0" data-testid="edit-export-sheet" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <SheetHeader className="p-6 border-b border-slate-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <SheetTitle className="font-heading text-white">Edit Export - {exportData.container_number || exportData.booking_number}</SheetTitle>
                {isDirty && (
                  <Badge
                    variant="outline"
                    className="border-amber-500/60 bg-amber-500/10 text-amber-300 text-[10px] font-medium animate-pulse"
                    data-testid="unsaved-indicator"
                  >
                    ● Unsaved
                  </Badge>
                )}
              </div>
              <Badge variant="outline" className="font-mono text-xs">{exportData.job_number}</Badge>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleSave} disabled={saving} size="sm" className="bg-orange-500 hover:bg-orange-400" data-testid="save-changes-btn">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Save
              </Button>
              <Button
                onClick={async () => {
                  // handleSave already calls onOpenChange(false) on success;
                  // we just need to NOT call it again on failure. Read the
                  // returned boolean to confirm success before any extra work.
                  await handleSave();
                }}
                disabled={saving}
                size="sm"
                className="bg-green-600 hover:bg-green-500"
                data-testid="save-and-exit-export-btn"
              >
                Save and Exit
              </Button>
              <Button onClick={() => setDeleteDialogOpen(true)} variant="outline" size="sm" className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white" data-testid="delete-export-btn">
                <Trash2 className="h-4 w-4 mr-1" />Delete
              </Button>
              <Button onClick={handleConfirmClose} variant="outline" size="sm" className="border-slate-700" data-testid="exit-export-btn">Exit</Button>
            </div>
          </SheetHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full justify-start rounded-none border-b border-slate-800 bg-transparent h-auto p-0">
              <TabsTrigger value="shipment-status" className="rounded-none border-b-2 border-transparent data-[state=active]:border-green-500 data-[state=active]:bg-green-500/10 px-4 py-3 text-slate-400 data-[state=active]:text-white">Shipment Status</TabsTrigger>
              <TabsTrigger value="documents" className="rounded-none border-b-2 border-transparent data-[state=active]:border-cyan-500 data-[state=active]:bg-cyan-500/10 px-4 py-3 text-slate-400 data-[state=active]:text-white">Documents ({documents.length})</TabsTrigger>
              <TabsTrigger value="dispatch-schedule" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-blue-500/10 px-4 py-3 text-slate-400 data-[state=active]:text-white">Dispatch Schedule</TabsTrigger>
              <TabsTrigger value="notes" className="rounded-none border-b-2 border-transparent data-[state=active]:border-purple-500 data-[state=active]:bg-purple-500/10 px-4 py-3 text-slate-400 data-[state=active]:text-white">Notes</TabsTrigger>
              <TabsTrigger value="edit-logs" className="rounded-none border-b-2 border-transparent data-[state=active]:border-amber-500 data-[state=active]:bg-amber-500/10 px-4 py-3 text-slate-400 data-[state=active]:text-white">Edit Logs</TabsTrigger>
              <TabsTrigger value="event-logs" className="rounded-none border-b-2 border-transparent data-[state=active]:border-indigo-500 data-[state=active]:bg-indigo-500/10 px-4 py-3 text-slate-400 data-[state=active]:text-white">Event Logs ({eventLogs.length})</TabsTrigger>
            </TabsList>

            {/* Shipment Status Tab - Same as Import */}
            <TabsContent value="shipment-status" className="p-6">
              <StatusHistoryTab 
                statusHistory={statusHistory} 
                formData={formData} 
                onChangeStatus={openLocationChangeModal}
                onDeleteEntry={handleDeleteStatusEntry}
                formatDateTime={formatDateTime} 
              />
            </TabsContent>

            {/* Documents Tab */}
            <TabsContent value="documents" className="p-6">
              <DocumentsTab
                documents={documents}
                uploading={uploading}
                onUpload={handleDocumentUpload}
                onDelete={handleDocumentDelete}
                onDownload={handleDocumentDownload}
                onPreview={handleDocumentPreview}
                fileInputRef={fileInputRef}
              />
            </TabsContent>

            {/* Dispatch Schedule Tab */}
            <TabsContent value="dispatch-schedule" className="p-6">
              <ExportDispatchScheduleTab
                exportData={exportData}
                drivers={masterData?.drivers || []}
                terminals={masterData?.terminals || []}
                yards={masterData?.yards || []}
                api={api}
                onDispatchCreated={() => {
                  toast.success('Dispatch created successfully');
                  onSave?.();
                }}
              />
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes" className="p-6">
              <NotesTab
                notes={notes}
                onAddNote={handleAddNote}
                onDeleteNote={handleDeleteNote}
                formatDateTime={formatDateTime}
              />
            </TabsContent>

            {/* Edit Logs Tab */}
            <TabsContent value="edit-logs" className="p-6">
              <EditLogsTab
                editLogs={editLogs}
                formatDateTime={formatDateTime}
              />
            </TabsContent>

            {/* Event Logs Tab */}
            <TabsContent value="event-logs" className="p-6">
              <EventLogsTab
                eventLogs={eventLogs}
                formatDateTime={formatDateTime}
                timelineEvents={timelineEvents}
                timelineLoading={timelineLoading}
                timelineError={timelineError}
              />
            </TabsContent>
          </Tabs>

          {/* Form fields shown below tabs when on Shipment Status tab - matches Import layout */}
          {activeTab === 'shipment-status' && (
            <div className="p-6 border-t border-slate-800 space-y-6">
              {/* Container Info */}
              <div className="space-y-4">
                <h3 className="font-heading font-medium text-white border-b border-slate-800 pb-2">Container Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Container</Label>
                    <Input value={formData.container_number || ''} onChange={(e) => setFormData(prev => ({ ...prev, container_number: e.target.value.toUpperCase() }))} placeholder="ABCD1234567" className="bg-slate-950 border-slate-800 text-white font-mono" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Booking Number</Label>
                    <Input value={formData.booking_number || ''} onChange={(e) => setFormData(prev => ({ ...prev, booking_number: e.target.value }))} className="bg-slate-950 border-slate-800 text-white" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Size</Label>
                    <Select value={formData.container_size || 'none'} onValueChange={(v) => setFormData(prev => ({ ...prev, container_size: v === 'none' ? '' : v }))}>
                      <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue placeholder="Select size" /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="none">Select size</SelectItem>
                        {masterData?.container_types?.length > 0 ? (
                          masterData.container_types.map(ct => (
                            <SelectItem key={ct.id} value={ct.code}>{ct.code} - {ct.name}</SelectItem>
                          ))
                        ) : (
                          <>
                            <SelectItem value="20GP">20GP (Standard)</SelectItem>
                            <SelectItem value="20RF">20RF (Reefer)</SelectItem>
                            <SelectItem value="20OT">20OT (Open Top)</SelectItem>
                            <SelectItem value="40GP">40GP (Standard)</SelectItem>
                            <SelectItem value="40HQ">40HQ (High Cube)</SelectItem>
                            <SelectItem value="40RF">40RF (Reefer)</SelectItem>
                            <SelectItem value="40OT">40OT (Open Top)</SelectItem>
                            <SelectItem value="45HQ">45HQ (High Cube)</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">SSL</Label>
                    <Select value={formData.ssl || 'none'} onValueChange={(v) => setFormData(prev => ({ ...prev, ssl: v === 'none' ? '' : v }))}>
                      <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue placeholder="Select SSL" /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="none">Select SSL</SelectItem>
                        {ssls.map(ssl => <SelectItem key={ssl.id} value={ssl.code}>{ssl.code || ssl.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Terminal & Equipment */}
              <div className="space-y-4">
                <h3 className="font-heading font-medium text-white border-b border-slate-800 pb-2">Terminal & Equipment</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Pickup Terminal</Label>
                    <Select value={formData.pickup_terminal_id || 'none'} onValueChange={(v) => {
                      const val = v === 'none' ? '' : v;
                      const terminal = masterData?.terminals?.find(t => t.id === val);
                      setFormData(prev => ({ ...prev, pickup_terminal_id: val, pickup_terminal_name: terminal?.name || '' }));
                    }}>
                      <SelectTrigger className="bg-slate-950 border-slate-800 text-white" data-testid="pickup-terminal-select"><SelectValue placeholder="Select pickup terminal" /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800 max-h-[300px]">
                        <SelectItem value="none">Select terminal</SelectItem>
                        {(masterData?.terminals || []).map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Return Terminal</Label>
                    <Select value={formData.return_terminal_id || 'none'} onValueChange={(v) => {
                      const val = v === 'none' ? '' : v;
                      const terminal = masterData?.terminals?.find(t => t.id === val);
                      setFormData(prev => ({ ...prev, return_terminal_id: val, return_terminal_name: terminal?.name || '' }));
                    }}>
                      <SelectTrigger className="bg-slate-950 border-slate-800 text-white" data-testid="return-terminal-select"><SelectValue placeholder="Select return terminal" /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800 max-h-[300px]">
                        <SelectItem value="none">Select terminal</SelectItem>
                        {(masterData?.terminals || []).map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Yard</Label>
                    <Select value={formData.yard_id || 'none'} onValueChange={(v) => {
                      const val = v === 'none' ? '' : v;
                      const yard = masterData?.yards?.find(y => y.id === val);
                      setFormData(prev => ({ ...prev, yard_id: val, yard_name: yard?.name || '' }));
                    }}>
                      <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue placeholder="Select yard" /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="none">Select yard</SelectItem>
                        {(masterData?.yards || []).map(y => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Chassis Type</Label>
                    <Select value={formData.chassis_type || 'none'} onValueChange={(v) => setFormData(prev => ({ ...prev, chassis_type: v === 'none' ? '' : v }))}>
                      <SelectTrigger className="bg-slate-950 border-slate-800 text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800">
                        <SelectItem value="none">Select chassis</SelectItem>
                        <SelectItem value="OWN">OWN</SelectItem>
                        <SelectItem value="POOL">POOL</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Chassis Number</Label>
                    <Input value={formData.chassis_number || ''} onChange={(e) => setFormData(prev => ({ ...prev, chassis_number: e.target.value }))} placeholder="Chassis #" className="bg-slate-950 border-slate-800 text-white" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Destination Port</Label>
                    <Input value={formData.destination_port || ''} onChange={(e) => setFormData(prev => ({ ...prev, destination_port: e.target.value }))} className="bg-slate-950 border-slate-800 text-white" />
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="space-y-4">
                <h3 className="font-heading font-medium text-white border-b border-slate-800 pb-2">Dates</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Cutoff Date</Label>
                    <Input type="datetime-local" value={formData.cutoff_date || ''} onChange={(e) => setFormData(prev => ({ ...prev, cutoff_date: e.target.value }))} className="bg-slate-950 border-slate-800 text-white" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">ERD (Early Return Date)</Label>
                    <Input type="datetime-local" value={formData.erd || ''} onChange={(e) => setFormData(prev => ({ ...prev, erd: e.target.value }))} className="bg-slate-950 border-slate-800 text-white" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Load Ready</Label>
                    <Input type="datetime-local" value={formData.load_rdy || ''} onChange={(e) => setFormData(prev => ({ ...prev, load_rdy: e.target.value }))} className="bg-slate-950 border-slate-800 text-white" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Load Return</Label>
                    <Input type="datetime-local" value={formData.load_return || ''} onChange={(e) => setFormData(prev => ({ ...prev, load_return: e.target.value }))} className="bg-slate-950 border-slate-800 text-white" />
                  </div>
                </div>
              </div>

              {/* Customer & Delivery - Same as Import */}
              <div className="space-y-4">
                <h3 className="font-heading font-medium text-white border-b border-slate-800 pb-2">Customer & Delivery</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2">
                    <Label className="text-slate-300">Customer</Label>
                    <Select value={formData.customer_id || 'none'} onValueChange={(v) => handleCustomerChange(v === 'none' ? '' : v)}>
                      <SelectTrigger className="bg-slate-950 border-slate-800 text-white" data-testid="edit-customer-select">
                        <SelectValue placeholder="Select customer" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800 max-h-[200px]">
                        <SelectItem value="none">Select customer</SelectItem>
                        {customers.map(customer => (
                          <SelectItem key={customer.id} value={customer.id} className="text-white">
                            {customer.name} {customer.code ? `(${customer.code})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {selectedCustomer && (
                    <div className="space-y-2 col-span-2">
                      <Label className="text-slate-300">Delivery Address</Label>
                      <Select value={formData.address_id || 'none'} onValueChange={(v) => handleAddressChange(v === 'none' ? '' : v)}>
                        <SelectTrigger className="bg-slate-950 border-slate-800 text-white" data-testid="edit-address-select">
                          <SelectValue placeholder="Select delivery address" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-800 max-h-[200px]">
                          <SelectItem value="none">Select address</SelectItem>
                          {selectedCustomer.addresses?.map(addr => (
                            <SelectItem key={addr.id} value={addr.id} className="text-white">
                              <div className="flex flex-col">
                                <span>{addr.label || addr.address}</span>
                                <span className="text-xs text-slate-400">{addr.city}, {addr.state} {addr.zip_code}</span>
                              </div>
                            </SelectItem>
                          ))}
                          {(!selectedCustomer.addresses || selectedCustomer.addresses.length === 0) && (
                            <div className="p-2 text-slate-400 text-sm">No addresses available</div>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {formData.delivery_address && (
                    <div className="col-span-2 p-3 bg-slate-800/30 rounded-sm">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2 col-span-2">
                          <Label className="text-slate-400 text-xs">Address</Label>
                          <Input value={formData.delivery_address || ''} onChange={(e) => setFormData(prev => ({ ...prev, delivery_address: e.target.value }))} className="bg-slate-950 border-slate-800 text-white" placeholder="Street address" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-slate-400 text-xs">City</Label>
                          <Input value={formData.delivery_city || ''} onChange={(e) => setFormData(prev => ({ ...prev, delivery_city: e.target.value }))} className="bg-slate-950 border-slate-800 text-white" />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-slate-400 text-xs">State/Zip</Label>
                          <div className="flex gap-2">
                            <Input value={formData.delivery_state || ''} onChange={(e) => setFormData(prev => ({ ...prev, delivery_state: e.target.value }))} placeholder="State" className="bg-slate-950 border-slate-800 text-white w-20" />
                            <Input value={formData.delivery_zip || ''} onChange={(e) => setFormData(prev => ({ ...prev, delivery_zip: e.target.value }))} placeholder="ZIP" className="bg-slate-950 border-slate-800 text-white" />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Shipper Info - Export specific */}
              <div className="space-y-4">
                <h3 className="font-heading font-medium text-white border-b border-slate-800 pb-2">Shipper Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-300">Shipper Name</Label>
                    <Input value={formData.shipper_name || ''} onChange={(e) => setFormData(prev => ({ ...prev, shipper_name: e.target.value }))} className="bg-slate-950 border-slate-800 text-white" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Shipper Address</Label>
                    <Input value={formData.shipper_address || ''} onChange={(e) => setFormData(prev => ({ ...prev, shipper_address: e.target.value }))} className="bg-slate-950 border-slate-800 text-white" />
                  </div>
                </div>
              </div>

              {/* Remarks */}
              <div className="space-y-2">
                <Label className="text-slate-300">Remarks</Label>
                <Textarea value={formData.remarks || ''} onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))} placeholder="Additional notes..." className="bg-slate-950 border-slate-800 text-white min-h-[80px]" />
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Location Change Modal - Same as Import dashboard */}
      <LocationChangeModal
        open={locationChangeModalOpen}
        onOpenChange={setLocationChangeModalOpen}
        shipment={exportData}
        terminals={masterData?.terminals || []}
        yards={masterData?.yards || []}
        drivers={masterData?.drivers || []}
        onUpdate={handleLocationStatusUpdate}
        api={api}
        entityType="export"
      />

      {/* Delete Confirm Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        exportData={exportData}
        deleting={deleting}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default EditExportSheet;
